import type { ShopifyResultQuestionSelection } from '@testfeed/shopify-contracts'
import { withShopifyAppContext } from '../../resultsNavigation'
/** Loads the current results view; the server owns its states, sections and available actions. */
import { useState } from 'react'
import { useDirectReportRead } from '../../hooks/outside/useDirectReportRead'
import type { ShopifyAppBridge } from '../../shopifyApiFetch'
import { resolveMerchantErrorMessage } from '../../merchantErrors'
import { ShopifyBackActionButton } from '../ShopifyPageActionButtons'
import { ShopifyPageSkeleton } from '../ShopifyPageSkeleton'
import { ServerResultProduct } from '../results/ServerResultProduct'
import { ServerResultMessages } from '../results/ServerResultsSections'
import { DirectStudyDeletion } from './DirectStudyDeletion'
import { DirectStudyDeleteControl } from './DirectStudyDeleteControl'
import { DirectStudyResultsBody } from './DirectStudyResultsBody'
import { useDirectStudyResults } from '../../hooks/useDirectStudyResults'
import { useResultPageNavigation } from '../../hooks/outside/useResultPageNavigation'
import { ResultNavigationContext, ResultNavigationButton } from '../results/ResultNavigation'
import { trackShopifyAction } from '../../shopifyUiTracking'
import { NextTestDraftContext } from '../results/NextTestDraftAction'
import { createStudyAuthoring } from '../../hooks/study-builder/studyAuthoringClient'
import { nextTestsCopy as copy } from '@testfeed/product-language'

export function DirectStudyResults({
  shopify,
  studyId,
  runId,
  questionSelection,
  onBack,
}: {
  shopify: ShopifyAppBridge
  studyId: string
  runId: string | null
  questionSelection?: ShopifyResultQuestionSelection
  onBack: () => void
}) {
  const [selectedTab, setSelectedTab] = useState<{ selectionKey: string | null; id: string | null }>({
    selectionKey: null,
    id: null,
  })
  const { read, lifecycle, source, visit, selectionKey, current, failed, retry } = useDirectStudyResults(
    shopify,
    studyId,
    runId,
    questionSelection,
  )
  const acceptedSelection = source?.selectionKey ?? null
  let tab: string | null = null
  if (selectedTab.selectionKey !== acceptedSelection) setSelectedTab({ selectionKey: acceptedSelection, id: null })
  else tab = selectedTab.id
  const view = source?.results?.view
  const readRunId = view?.state === 'report' ? view.runId : null
  useDirectReportRead(readRunId)
  const navigate = useResultPageNavigation({ visit, selectionKey, ready: current, available: !!view })
  let answerStatus: 'ready' | 'loading' | 'error' = 'ready'
  if (!current) {
    answerStatus = 'loading'
    if (failed && !read.isFetching) answerStatus = 'error'
  }
  const navigation = {
    navigate,
    answers: {
      status: answerStatus,
      message: resolveMerchantErrorMessage(read.error, 'Answers could not be loaded. Try again.'),
      retry,
    },
  }
  const nextTestDraft = {
    lifecycle,
    create: async (
      selection: NonNullable<
        import('@testfeed/shopify-contracts').ShopifyResultPhrases['proposalGroups'][number]['proposals'][number]['createDraft']
      >['selection'],
    ) => {
      if (!source?.authoring || !current) return null
      const result = await lifecycle.run('create-next-test', (signal) =>
        createStudyAuthoring(
          shopify,
          {
            creationIntentId: 'next-test',
            draft: source.authoring.authoring.draft,
            nextTest: selection,
          },
          signal,
        ),
      )
      return result.status === 'done' ? result.value : null
    },
  }
  const actionFailures = lifecycle.feedback
    .filter((failure) => failure.operation === 'create-next-test')
    .map((failure) => ({
      ...failure,
      message: failure.message ?? copy.createFailed,
    }))

  if (source?.deletion)
    return <DirectStudyDeletion shopify={shopify} receipt={source.deletion} onBack={onBack} lifecycle={lifecycle} />

  const backAction = view?.actions.find((action) => action.id === 'back')
  const editAction = view?.actions.find((action) => action.id === 'edit')
  const deleteAction = view?.actions.find((action) => action.id === 'delete')
  const retryAction = view?.actions.find((action) => action.id === 'retry_read')
  let body = <ShopifyPageSkeleton accessibilityLabel="Loading test results" />
  if (failed && !source)
    body = (
      <s-banner tone="critical">
        <s-text>{resolveMerchantErrorMessage(read.error, 'Results could not be loaded.')}</s-text>
        <s-button onClick={retry}>Try again</s-button>
      </s-banner>
    )
  else if (view)
    body = (
      <DirectStudyResultsBody
        view={view}
        tab={tab}
        onSelectTab={(next) =>
          trackShopifyAction(shopify, 'select-tab', () => setSelectedTab({ selectionKey: acceptedSelection, id: next }))
        }
      />
    )

  return (
    <ResultNavigationContext.Provider value={navigation}>
      <s-page heading={view?.heading ?? 'Test results'} inlineSize="large">
        {backAction && <ShopifyBackActionButton action={{ content: backAction.label, onAction: onBack }} />}
        {deleteAction && source?.authoring && (
          <DirectStudyDeleteControl
            shopify={shopify}
            studyId={studyId}
            state={source.authoring.state}
            lifecycle={lifecycle}
            title={view?.heading ?? 'Test'}
            label={deleteAction.label}
            onDeleted={onBack}
          />
        )}
        {editAction?.href && (
          <ResultNavigationButton slot="secondary-actions" href={withShopifyAppContext(editAction.href)}>
            {editAction.label}
          </ResultNavigationButton>
        )}
        {view && <ServerResultMessages messages={view.messages} />}
        {failed && source && current && (
          <s-banner tone="warning">
            <s-text>Couldn’t refresh the results. Showing the last loaded progress or report.</s-text>
            <s-button onClick={retry}>Try again</s-button>
          </s-banner>
        )}
        {view && <ServerResultProduct product={view.product} />}
        {view && view.pastRuns.length > 0 && (
          <s-section heading="Past runs">
            <s-stack direction="inline" gap="base">
              {view.pastRuns.map((item) => (
                <ResultNavigationButton key={item.id} href={withShopifyAppContext(item.href)}>
                  {new Date(item.requestedAt).toLocaleString()} · {item.status}
                </ResultNavigationButton>
              ))}
            </s-stack>
          </s-section>
        )}
        {actionFailures.map((failure) => (
          <s-banner key={failure.operation} tone="critical">
            {failure.message}
          </s-banner>
        ))}
        <NextTestDraftContext.Provider value={nextTestDraft}>{body}</NextTestDraftContext.Provider>
        {retryAction && <s-button onClick={retry}>{retryAction.label}</s-button>}
      </s-page>
    </ResultNavigationContext.Provider>
  )
}
